import { Search, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useEventCallback } from '@/hooks/use-event-callback'

import { useHotkey } from '@/hooks/use-hotkeys'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export interface SearchInputProps {
    value: string
    /** Called after the debounce interval, not on every keystroke. */
    onChange: (next: string) => void
    placeholder?: string
    /** Registers `/` to focus this input. Only one search input per page should claim it. */
    hotkey?: boolean
    debounceMs?: number
    className?: string
    'aria-label'?: string
}

/**
 * Local text state with a debounced commit, so typing stays responsive while the
 * URL and the query only move once the operator pauses. The legacy app debounced
 * per page at 300ms or 350ms or not at all; this is the single implementation.
 */
export function SearchInput({
    value,
    onChange,
    placeholder = 'Search',
    hotkey = false,
    debounceMs = 300,
    className,
    'aria-label': ariaLabel
}: SearchInputProps) {
    const inputRef = useRef<HTMLInputElement>(null)
    const commit = useEventCallback(onChange)

    // An external change -- a cleared chip, a back-button press, a saved view --
    // has to win over the local draft, or the input and the URL disagree. Comparing
    // against the last seen value during render means the input never shows one
    // frame of the stale text.
    const [state, setState] = useState({ committed: value, draft: value })
    if (state.committed !== value) setState({ committed: value, draft: value })
    const draft = state.draft
    const setDraft = (next: string) => setState({ committed: value, draft: next })

    useEffect(() => {
        if (draft === value) return
        const timer = window.setTimeout(() => commit(draft), debounceMs)
        return () => window.clearTimeout(timer)
    }, [commit, draft, value, debounceMs])

    useHotkey('/', () => inputRef.current?.focus(), { enabled: hotkey })

    return (
        <div className={cn('relative', className)}>
            <Search
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
            />
            <Input
                ref={inputRef}
                type="search"
                value={draft}
                onChange={event => setDraft(event.target.value)}
                onKeyDown={event => {
                    if (event.key === 'Escape' && draft !== '') {
                        // Escape clears rather than blurring: the operator's next move
                        // after a dead-end search is almost always another search.
                        event.preventDefault()
                        event.stopPropagation()
                        setDraft('')
                        commit('')
                    }
                    if (event.key === 'Enter') {
                        event.preventDefault()
                        commit(draft)
                    }
                }}
                placeholder={placeholder}
                aria-label={ariaLabel ?? placeholder}
                className="pl-8 pr-8 [&::-webkit-search-cancel-button]:hidden"
            />
            {draft !== '' && (
                <button
                    type="button"
                    onClick={() => {
                        setDraft('')
                        commit('')
                        inputRef.current?.focus()
                    }}
                    aria-label="Clear search"
                    className={cn(
                        // Real growth rather than hit-slop: `touch-target` sets
                        // `position: relative`, which this control cannot have -- it is
                        // positioned inside the field. Padding grows instead, to 30px
                        // and still comfortably within the input's own box.
                        'absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 pointer-coarse:p-2',
                        'text-muted-foreground transition-colors hover:text-foreground',
                        'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring'
                    )}>
                    <X className="size-3.5" />
                </button>
            )}
        </div>
    )
}
